/**
 * Contact Service
 *
 * Provides API methods for managing contacts — lightweight on-site contact
 * people linked to companies. Contacts are NOT system users; they represent
 * site managers, superintendents, or other personnel at a job site.
 *
 * Contacts are reusable across multiple work orders for the same company,
 * eliminating redundant data entry for frequently-used site contacts.
 *
 * Backend endpoints:
 *	 GET	/api/contacts						 — List/filter contacts (paginated)
 *	 GET	/api/companies/:companyId/contacts	 — List contacts for a specific company
 *	 POST	/api/contacts						 — Create a new contact
 *	 GET	/api/contacts/:id					 — Get a single contact
 *	 PATCH	/api/contacts/:id					 — Update a contact
 *	 DELETE /api/contacts/:id					 — Soft-deactivate a contact
 *
 * All endpoints require authentication (Sanctum Bearer token).
 */

import { api } from './api';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Represents an on-site contact person as returned by the API.
 *
 * @property id		   - Unique contact identifier
 * @property companyId - The company this contact belongs to
 * @property firstName - Contact's first name
 * @property lastName  - Contact's last name
 * @property fullName  - Computed "First Last" display name (appended by backend)
 * @property phone	   - Phone number, null if not provided
 * @property email	   - Email address, null if not provided
 * @property title	   - Job title (e.g., "Site Manager"), null if not provided
 * @property notes	   - Free-form notes about this contact, null if not provided
 * @property status	   - 'active' or 'inactive' — inactive contacts are hidden from dropdowns
 */
export interface Contact {
	id: number;
	companyId: number;
	firstName: string;
	lastName: string;
	fullName: string;
	phone: string | null;
	email: string | null;
	title: string | null;
	notes: string | null;
	status: string;
}

/**
 * Payload for creating a new contact. Sent as snake_case to match
 * the Laravel backend's expected field names.
 *
 * Names may be omitted when user_id is set — the backend then derives
 * first/last/phone/email from the linked user record, and reuses the
 * existing contact for that (company, user) pair instead of creating a
 * duplicate (find-or-create).
 */
export interface CreateContactData {
	first_name?: string;
	last_name?: string;
	company_id: number | string;
	phone?: string;
	email?: string;
	title?: string;
	notes?: string;
	user_id?: number | string;
}

/**
 * Payload for updating an existing contact. All fields optional (PATCH
 * semantics). Setting company_id reassigns the contact to a different
 * customer — company-scoped users may only reassign within their own
 * accessible companies (the backend returns 403 otherwise).
 */
export interface UpdateContactData {
	first_name?: string;
	last_name?: string;
	company_id?: number | string;
	phone?: string | null;
	email?: string | null;
	title?: string | null;
	notes?: string | null;
	status?: 'active' | 'inactive';
}

/**
 * Query filters for listing contacts.
 */
export interface ContactFilters {
	company_id?: string;
	search?: string;
	/** active (default) | inactive | all */
	status?: string;
	/** '1' to include work_orders_count + inspections_count per row */
	with_counts?: string;
	per_page?: string;
	page?: string;
}

/* ============================================
 * SERVICE METHODS
 * ============================================ */

/**
 * Contact service methods.
 * Each method maps to a specific backend API endpoint.
 */
export const contactService = {
	/**
	 * GET /api/companies/:companyId/contacts
	 * Fetch all active contacts for a specific company. This is the primary
	 * method used by the work order form to populate the contact dropdown
	 * after a company is selected.
	 *
	 * @param companyId - The company ID to fetch contacts for
	 * @returns Array of contact objects for the given company
	 */
	getByCompany: (companyId: string) =>
		/* per_page=999: the endpoint paginates at 25 by default, which silently
		   truncated the "Existing Contact" dropdown for any customer with more
		   than 25 contacts on file — they looked like they had never been saved. */
		api.get<any>(`/companies/${companyId}/contacts`, { per_page: '999' }),

	/**
	 * POST /api/contacts
	 * Create a new contact record linked to a company. Used when the user
	 * enters a new on-site contact in the work order creation form.
	 *
	 * @param data - The contact data to create (first_name, last_name, company_id required)
	 * @returns The created contact record with company and users loaded
	 */
	create: (data: CreateContactData) =>
		api.post<any>('/contacts', data),

	/**
	 * GET /api/contacts
	 * List all contacts with optional filters. Supports pagination, company
	 * filtering, status filtering (active/inactive/all), opt-in reference
	 * counts, and text search across name/email fields.
	 *
	 * @param filters - Optional query filters (company_id, search, status, with_counts, per_page, page)
	 * @returns Paginated list of contacts
	 */
	getAll: (filters?: ContactFilters) =>
		api.get<any>('/contacts', filters as Record<string, string> | undefined),

	/**
	 * GET /api/contacts/:id
	 * Fetch a single contact with its company and linked users.
	 */
	getById: (id: number | string) =>
		api.get<any>(`/contacts/${id}`),

	/**
	 * PATCH /api/contacts/:id
	 * Update a contact. Passing company_id reassigns the contact to a
	 * different customer; passing status toggles active/inactive.
	 */
	update: (id: number | string, data: UpdateContactData) =>
		api.patch<any>(`/contacts/${id}`, data),
};
